import { makeStyles } from '@material-ui/core/styles';
import { useState, useRef, useMemo, useEffect } from 'react';
import { Box, Button, IconButton, Popover } from '@material-ui/core';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import GroupItem from './GroupItem';

const useStyles = makeStyles(theme => ({
    labelInputSelectVar: {
        fontSize: '16px',
        marginBottom: '3px'
    },
    containerLabelIconOpen: {
        borderBottom: `0.2px solid ${'#00000082'}`,
        cursor: 'pointer'
    },
    containerGroups: {
        ...theme.scrollbars.scrollsSettings,
        maxHeight: '500px',
        backgroundColor: 'white',
        borderRadius: '2px',
        boxShadow: '10px 8px 25px -2px #02020240',
        '&::-webkit-scrollbar': {
            ...theme.scrollbars.webkitScrollbar,
        },
        '&::-webkit-scrollbar-track': {
            ...theme.scrollbars.webkitScrollbarTrack,
        },
        '&::-webkit-scrollbar-thumb': {
            background: theme.palette.primary.main,
        },
        '@media (max-width: 960px)': {
            width: '80%',
        },
    },
    iconButtonArrowDrow: {
        '&.MuiIconButton-root': {
            paddingRight: 0,
            paddingBottom: 0
        }
    },
    placeHolder: {
        all: 'unset',
        fontSize: '17px',
        color: '#7c7c7d',
    },
    footerActions: {
        position: 'sticky',
        bottom: 0,
        backgroundColor: '#fff',
        padding: '8px 12px',
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'center',
        zIndex: 3,
    }
}));

const disabledElement = {
    pointerEvents: 'none',
    opacity: 0.5,
    color: '#ccc',
};

const VariableSelectorReport = ({
    variables = [],
    disabledSelect = false,
    dataset,
    setDataset,
    hasSomeVirtualNodes,
    objectCatalog
}) => {
    const classes = useStyles();
    const [openSelect, setOpenSelect] = useState(false);
    const [groupTitleKey, setGroupTitleKey] = useState('');
    const componentRef = useRef(null);
    const placeHolder = 'Seleccionar variables'
    const [anchorEl, setAnchorEl] = useState(null);
    const [anchorWidth, setAnchorWidth] = useState(null);

    /* =======================
       Agrupación
    ======================= */
    const { grouped, ungrouped } = useMemo(() => {
        const groupedMap = {};
        const ungrouped = [];

        variables.forEach(v => {
            if (v.key === 'VARIABLES_PERSONALIZADAS_AUTOMATE') {
                return;
            }
            if (v.group?.name) {
                const name = v.group.name;
                if (!groupedMap[name]) {
                    groupedMap[name] = {
                        key: name,
                        name,
                        children: []
                    };
                }
                groupedMap[name].children.push(v);
            } else {
                ungrouped.push(v);
            }
        });

        return {
            grouped: Object.values(groupedMap),
            ungrouped
        };
    }, [variables]);

    const handleToggleVariable = (item, checked) => {
        if (!item) return;

        setDataset(prev => {
            const current = Array.isArray(prev.variables) ? prev.variables : [];

            // =========================
            // AUTOMATE VARIABLES
            // =========================
            if (item.isVarAutomate) {
                const varToAdd = {
                    ...item,
                    val: `${item.group?.key} ${item?.key}`,
                };

                const exists = current.some(
                    v =>
                        v.key === varToAdd.key &&
                        v.groupKey === varToAdd.groupKey &&
                        v.val === varToAdd.val
                );

                const next = checked
                    ? (exists ? current : [...current, varToAdd])
                    : current.filter(
                        v =>
                            !(
                                v.key === varToAdd.key &&
                                v.groupKey === varToAdd.groupKey &&
                                v.val === varToAdd.val
                            )
                    );

                return {
                    ...prev,
                    variables: next,
                };
            }

            // =========================
            // NORMAL VARIABLES
            // =========================
            const value = item.key ?? item;
            const exists = current.includes(value);

            const next = checked
                ? (exists ? current : [...current, value])
                : current.filter(v => v !== value);

            return {
                ...prev,
                variables: next,
            };
        });
    };



    const handleAccept = () => {
        setOpenSelect(false);
        setAnchorEl(null);
    };

    const getVariableLabel = (v) => {
        if (typeof v === 'object') {
            return v.tag || v.name;
        }

        const found = variables.find(item => item.key === v);
        return found?.tag || found?.name || v;
    };


    return (
        <Box display="flex" width="100%" height="40px" >


            <Box
                display="flex"
                flexDirection={dataset?.variables?.length && placeHolder ? 'column' : 'row'}
                width="100%"
            >
                <Box
                    className={classes.containerLabelIconOpen}
                    onClick={(e) => {
                        if (disabledSelect) return;
                        setAnchorEl(e.currentTarget);
                        setAnchorWidth(e.currentTarget.clientWidth);
                        setOpenSelect(true);
                    }}
                    style={disabledSelect ? disabledElement : {}}
                    width="100%"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-end"
                >
                    {!dataset?.variables?.length && (
                        <input
                            value={placeHolder}
                            readOnly
                            className={classes.placeHolder}
                        />
                    )}

                    <Box className={classes.labelInputSelectVar}>
                        {dataset?.variables?.length
                            ? dataset.variables
                                .map(getVariableLabel)
                                .join(', ')
                            : ''}
                    </Box>

                    <IconButton className={classes.iconButtonArrowDrow}>
                        <ArrowDropDownIcon />
                    </IconButton>
                </Box>
            </Box>


            {/* SELECT ABIERTO */}
            {openSelect && (
                <Popover
                    open={openSelect}
                    anchorEl={anchorEl}
                    onClose={() => setOpenSelect(false)}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    PaperProps={{
                        className: classes.containerGroups,
                        style: {
                            width: anchorWidth ?? 'auto',
                        }
                    }}
                >
                    <Box
                        ref={componentRef}
                        width="100%"
                        maxHeight={"400px"}
                    >

                        {/* CON GRUPO */}
                        {grouped.map(group => (
                            <GroupItem
                                key={group.key}
                                element={group}
                                selectedValues={dataset.variables}
                                onToggle={handleToggleVariable}
                                groupTitleKey={groupTitleKey}
                                setGroupTitleKey={setGroupTitleKey}
                                hasSomeVirtualNodes={hasSomeVirtualNodes}
                                objectCatalog={objectCatalog}
                                dataset={dataset}
                            />
                        ))}

                        {/* SIN GRUPO */}
                        {ungrouped.map(v => (
                            <GroupItem
                                element={{
                                    key: v.key,
                                    name: v.tag || v.name,
                                    children: [v]
                                }}
                                selectedValues={dataset.variables}
                                onToggle={handleToggleVariable}
                                groupTitleKey={groupTitleKey}
                                setGroupTitleKey={setGroupTitleKey}
                                hasSomeVirtualNodes={hasSomeVirtualNodes}
                                objectCatalog={objectCatalog}
                                dataset={dataset}
                            />
                        ))}

                        {/* FOOTER FIJO */}
                        <Box className={classes.footerActions}>
                            <Button
                                className={classes.devicesBtn}
                                variant='contained'
                                color='primary'
                                onClick={handleAccept}
                            >

                                Aceptar
                            </Button>
                        </Box>
                    </Box>
                </Popover>
            )}
        </Box>
    );
};

export default VariableSelectorReport;
